import { OpenLevelNameEnum } from "common/enums/OpenLevelEnum";
import { listStickyNotes } from "common/functions/api/stickyNotes";
import { MainContextContainer } from "components/Main";
import { StickyNote } from "graphql/API";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { CheckStickyNotesCondition } from "../../../../common/interfaces/CheckStickyNotesCondition";
import { StickyNoteInputMode } from "../StickyNoteInputDialog/enums";

export const useCheckStickyNotesHooks = () => {
  const initSearchRef = useRef(true);

  const mainContext = useContext(MainContextContainer);

  const [condition, setCondition] = useState<CheckStickyNotesCondition>({
    openLevels: [OpenLevelNameEnum.社内, OpenLevelNameEnum.支店内],
    company: mainContext.company
  });

  const [openOpenLevelSelectDialog, setOpenOpenLevelSelectDialog] =
    useState(false);
  const [stickyNotes, setStickyNotes] = useState<
    (StickyNote & {
      ownerName: string;
      branchName: string;
    })[]
  >([]);

  const [clickedStickyNote, setClickedStickyNote] = useState<
    StickyNote | undefined
  >(undefined);
  const [inputMode, setInputMode] = useState<StickyNoteInputMode | null>(null);

  const [busy, setBusy] = useState(false);

  const stickyNotesCount = useMemo(() => stickyNotes.length, [stickyNotes]);

  const onOpenLevelsClick = useCallback(() => {
    setOpenOpenLevelSelectDialog(true);
  }, []);

  const onOpenLevelSelectDialogClose = useCallback(() => {
    setOpenOpenLevelSelectDialog(false);
  }, []);

  const onOpenLevelSelectDialogOkClick = useCallback(
    (openLevels: OpenLevelNameEnum[]) => {
      setCondition((condition) => ({ ...condition, openLevels }));
      setOpenOpenLevelSelectDialog(false);
    },
    []
  );

  const onSearchClick = useCallback(async () => {
    try {
      setBusy(true);

      const validatedCondition: CheckStickyNotesCondition = {
        ...condition,
        company: mainContext.company
      };

      const getStickyNotes = await listStickyNotes(validatedCondition);

      setStickyNotes(
        getStickyNotes.sort(
          (a, b) =>
            new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
            new Date(a.updatedAt ?? a.createdAt ?? 0).getTime()
        )
      );
    } finally {
      setBusy(false);
    }
  }, [condition, mainContext.company]);

  const onStickyNoteClick = useCallback(
    (stickyNote: StickyNote, mode: StickyNoteInputMode) => {
      setClickedStickyNote(stickyNote);
      setInputMode(mode);
    },
    [setClickedStickyNote, setInputMode]
  );

  const onStickyNotesChange = useCallback(
    (
      changedStickyNotes: {
        stickyNote: StickyNote & {
          ownerName?: string;
        };
        mode: StickyNoteInputMode | null;
      }[]
    ) => {
      changedStickyNotes.forEach((changedStickyNote) => {
        const currentStickyNotes = [...stickyNotes];
        const operatedStickyNoteIndex = currentStickyNotes.findIndex(
          (x) => x.id === changedStickyNote.stickyNote.id
        );
        switch (changedStickyNote.mode) {
          case StickyNoteInputMode.Delete:
            setStickyNotes((stickyNotes) =>
              stickyNotes
                .filter((x) => x.id !== changedStickyNote.stickyNote.id)
                .slice()
            );
            break;
          case StickyNoteInputMode.Update:
            setStickyNotes((stickyNotes) => {
              const newStickyNotes = stickyNotes.slice();

              newStickyNotes[operatedStickyNoteIndex] = {
                ...newStickyNotes[operatedStickyNoteIndex],
                ...changedStickyNote.stickyNote
              };

              return newStickyNotes;
            });
            break;
        }
      });

      setClickedStickyNote(undefined);
      setInputMode(null);
    },
    [stickyNotes]
  );

  const onInputDialogOkButtonClick = useCallback(
    (
      mode: StickyNoteInputMode,
      inputValues:
        | (StickyNote & {
            ownerName?: string;
          })
        | null
    ) => {
      if (inputValues) {
        onStickyNotesChange([
          {
            stickyNote: {
              ...inputValues,
              ownerName: mainContext.user?.name ?? "？"
            },
            mode
          }
        ]);
      }
    },
    [mainContext.user?.name, onStickyNotesChange]
  );

  const onInputClose = useCallback(() => {
    setClickedStickyNote(undefined);
    setInputMode(null);
  }, []);

  useEffect(() => {
    if (initSearchRef.current && mainContext.company?.id) {
      onSearchClick();
      initSearchRef.current = false;
    }
  }, [mainContext.company?.id, onSearchClick]);

  return {
    mainContext,

    condition,
    openOpenLevelSelectDialog,

    stickyNotes,
    stickyNotesCount,

    clickedStickyNote,
    inputMode,

    busy,

    onOpenLevelsClick,
    onOpenLevelSelectDialogClose,
    onOpenLevelSelectDialogOkClick,

    onSearchClick,

    onStickyNoteClick,
    onInputDialogOkButtonClick,
    onInputClose
  } as const;
};
